import { isCloudinaryMedia } from './cloudinary';
import { gql } from '@apollo/client';

// The paired result preserves attachment identity when a message has both fields.
export const REFRESH_MESSAGE_MEDIA_URLS = gql`
    query RefreshImageRecoveryMedia($data: RefreshMessageImageSignedUrlInput!) {
        refreshMessageMediaUrls(data: $data) {
            image { url }
            file { url }
        }
    }
`;

export const REFRESH_PREVIEW_IMAGE = gql`
    query RefreshPreviewImage($data: RefreshUrlMetaImageSignedUrlInput!) {
        refreshUrlMetaImageSignedUrl(data: $data)
    }
`;

export const mediaImageSources = media => [
    media?.thumbnail?.lg, media?.thumbnail?.xl, media?.thumbnail?.md,
    media?.thumbnail?.sm, media?.thumbnail?.original, media?.cdn_url, media?.url
];

export const messageImageRefresh = (messageId, mediaKind = 'image') => messageId && !String(messageId).startsWith('temp') ? {
    query: REFRESH_MESSAGE_MEDIA_URLS, variables: { data: { messageId } }, field: ['refreshMessageMediaUrls', mediaKind, 'url']
} : undefined;

export const urlMetaImageRecovery = (meta, isAuthor = false) => {
    const thumbnail = isAuthor ? meta?.author_image_thumbnail : meta?.thumbnail;
    const key = isAuthor ? meta?.author_image_name || meta?.author_image_public_id : meta?.image_name || meta?.public_id;
    return {
        sources: [thumbnail?.lg, thumbnail?.xl, thumbnail?.md, thumbnail?.sm, thumbnail?.original,
            ...(isAuthor ? [meta?.author_image_cdn_url, meta?.author_image, meta?.author_image_original]
                : [meta?.image_cdn_url, meta?.image])],
        refresh: key && !isCloudinaryMedia({ public_id: key, url: isAuthor ? meta?.author_image : meta?.image }) ? {
            query: REFRESH_PREVIEW_IMAGE, variables: { data: { publicId: key } }, field: 'refreshUrlMetaImageSignedUrl'
        } : undefined
    };
};

const parseSource = value => {
    try { return new URL(value); } catch { return null; }
};
const isS3Delivery = url => /^luckynote\.s3(?:[.-][a-z0-9-]+)*\.amazonaws\.com$/.test(url.hostname);
const isCdnDelivery = url => url.hostname.endsWith('.b-cdn.net');
const isStoredDelivery = url => url.protocol === 'https:' && (isS3Delivery(url) || isCdnDelivery(url));
const storagePath = key => key && `/${String(key).replace(/^\/+/, '')}`;

// External URLs retain their origin and query. Only known delivery hosts can
// match across signatures, and CDN/S3 equivalence requires the saved storage key.
const sameStoredAsset = (selected, candidate, key) => {
    if (!selected || !candidate || !isStoredDelivery(selected) || !isStoredDelivery(candidate)) return false;
    if (selected.pathname !== candidate.pathname) return false;
    if (selected.origin === candidate.origin) return true;
    return storagePath(key) === selected.pathname && isS3Delivery(selected) !== isS3Delivery(candidate);
};

export const workspaceImageRecovery = (message, source, preferred) => {
    const selected = parseSource(preferred);
    const assets = [];
    for (const mediaKind of ['image', 'file']) {
        const media = message?.[mediaKind];
        assets.push({ sources: mediaImageSources(media), key: media?.name,
            refresh: messageImageRefresh(message?.id, mediaKind) });
    }
    for (const meta of message?.urlMeta || []) {
        for (const entry of [meta, meta?.quoted_url_meta, ...(meta?.gallery || []), ...(meta?.quoted_url_meta?.gallery || [])]) {
            if (!entry) continue;
            if (entry.media && !/^video(?:\/|$)/i.test(String(entry.type || entry.media.type || entry.media.mime || ''))) {
                assets.push({ sources: mediaImageSources(entry.media), key: entry.image_name || entry.media.name,
                    refresh: entry.image_name ? urlMetaImageRecovery(entry).refresh : undefined });
            }
            for (const isAuthor of [false, true]) {
                assets.push({ ...urlMetaImageRecovery(entry, isAuthor),
                    key: isAuthor ? entry.author_image_name || entry.author_image_public_id : entry.image_name || entry.public_id });
            }
        }
    }
    // Prefer an exact URL match anywhere in the message over delivery equivalence.
    const exact = assets.find(asset => asset.sources.some(url => url &&
        (url === preferred || selected && parseSource(url)?.href === selected.href)));
    const matched = exact || assets.find(asset => asset.sources.some(url => sameStoredAsset(selected, parseSource(url), asset.key)));
    if (matched) return { sources: [preferred, ...matched.sources], refresh: matched.refresh };
    return { sources: [preferred, ...mediaImageSources(typeof source?.image === 'object' ? source.image : source), source?.src] };
};
